import React from 'react'

import { t } from '../../../common/i18n.js'
import { SignatureItem } from '../../../common/types/api.js'
import { Language } from '../../../common/types/i18n.js'
import { SignatureCreationProps } from '../../modules/signature-creation/signature-creation.js'
import { State, View } from '../../types.js'
import {
    SignatureFormData,
    signatureItemToFormData,
    ValidationErrorClass,
} from '../../utils/signature-form-utils.js'

export type GroupedSignatures = {
    playSignatures: SignatureItem[]
    nopeSignatures: SignatureItem[]
}

export function groupSignaturesByCompany(signatures: SignatureItem[]): GroupedSignatures {
    return signatures.reduce(
        (acc, sig) => {
            const company = sig.company?.toLowerCase().trim()
            if (company === 'play') {
                acc.playSignatures.push(sig)
            } else if (company === 'nope') {
                acc.nopeSignatures.push(sig)
            } else {
                console.warn(`Unknown company: ${sig.company}`)
            }
            return acc
        },
        { playSignatures: [], nopeSignatures: [] } as GroupedSignatures,
    )
}

export function getSignatureErrorMessage(
    error: unknown,
    mode: 'create' | 'edit',
    lang: Language,
): string {
    if (error instanceof ValidationErrorClass) {
        return error.message
    }
    return mode === 'create' ? t.signature.createError(lang) : t.signature.editError(lang)
}

export function getInitialSignatureFormData(
    view: View,
    mode: 'create' | 'edit',
    signatureId?: string,
    company?: 'play' | 'nope',
): SignatureFormData | undefined {
    if (mode === 'edit' && signatureId) {
        const signatures = view.state.signatures.data
        if (signatures?.data) {
            const signature = signatures.data.find((sig) => sig.id.toString() === signatureId)
            if (signature) {
                return signatureItemToFormData(signature)
            }
        }
        return undefined
    }

    if (mode === 'create' && company) {
        return {
            name: '',
            position: '',
            email: '',
            tags: company === 'nope' ? ['nope'] : ['play'],
            rows: [],
            imageUrl: undefined,
            company,
        }
    }

    return undefined
}

export function createSignatureCreationProps(
    mode: 'create' | 'edit',
    initialData: SignatureFormData | undefined,
    state: State,
    handleSave: (formData: SignatureFormData) => Promise<void>,
    handleCancel: () => void,
    handleNavigateBack: () => void,
    lang: Language,
): SignatureCreationProps {
    const s = t.signature
    return {
        children: React.createElement('div', {}, 'Signature Creation Form'),
        state,
        signature: {
            name: '',
            title: '',
            email: undefined,
            isPlay: false,
            isNope: false,
        },
        navigateBack: handleNavigateBack,
        backButtonProps: {
            text: s.backButton(lang),
            variant: 'link',
            size: 'medium',
        },
        titlePrefix: mode === 'create' ? s.createTitlePrefix(lang) : s.editTitlePrefix(lang),
        titlePlaySuffix: s.titlePlaySuffix(lang),
        titleNopeSuffix: s.titleNopeSuffix(lang),
        requiredFieldsText: s.requiredFields(lang),
        fields: {
            name: {
                label: s.nameLabel(lang),
                placeholder: s.namePlaceholder(lang),
            },
            position: {
                label: s.positionLabel(lang),
                placeholder: s.positionPlaceholder(lang),
            },
            email: {
                label: s.emailLabel(lang),
                placeholder: s.emailPlaceholder(lang),
            },
        },
        customRow: {
            titleFieldProps: {
                label: s.rowTitle(lang),
            },
            contentFieldProps: {
                label: s.rowContent(lang),
            },
            linkFieldProps: {
                label: s.rowLink(lang),
            },
        },
        buttons: {
            save: {
                text: mode === 'create' ? s.saveCreate(lang) : s.saveEdit(lang),
                variant: 'primary',
                size: 'medium',
            },
            cancel: {
                text: mode === 'create' ? s.cancelCreate(lang) : s.cancelEdit(lang),
                variant: 'alert',
                size: 'medium',
            },
            addRow: {
                text: s.addRow(lang),
                variant: 'primary',
                size: 'medium',
            },
            removeRow: {
                text: s.removeRow(lang),
                variant: 'alert',
                size: 'small',
            },
        },
        image: {
            uploadLabel: s.imageLabel(lang),
            selectButtonProps: {
                text: s.selectImage(lang),
                variant: 'primary',
                size: 'small',
            },
            removeButtonProps: {
                text: s.removeImage(lang),
                variant: 'alert',
                size: 'small',
            },
            noImageSelectedText: t.common.noImageSelected(lang),
        },
        previewTitle: s.preview(lang),
        toggleProps: {
            value: false,
            onChange: () => {},
            offText: s.toggleOff(lang),
            onText: s.toggleOn(lang),
        },
        multiSelectProps: {
            label: s.logos(lang),
            options: [
                { value: 'play', label: 'gotoAndPlay' },
                { value: 'nope', label: 'NOPE' },
                { value: 'alliance', label: 'Alliance' },
            ],
            value: [],
            onChange: () => {},
        },
        cropModalProps: {
            title: s.cropTitle(lang),
            lang,
            isOpen: false,
            onClose: () => {},
        },
        mode,
        initialData,
        onSave: handleSave,
        onCancel: handleCancel,
        isLoading: false,
        isLinkLabel: s.isLink(lang),
    }
}
